/*
 * Copyright 2026 North Carolina State University
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import NewApplication from './NewApplication';
import ResourceOnlyApplication from './ResourceOnlyApplication';
import ProjectChat from './ProjectChat';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/SingleApplicationReview.css';

const REVISION_STATUSES = ['Revision Required', 'Revised'];

/**
 * User-facing two-pane view for applications that admins sent back for revision.
 * Left: editable application form (save keeps Revision Required).
 * Right: activity chat with admins so they can discuss the requested changes.
 */
const UserRevisionReview = ({
  application: propApplication,
  user: propUser,
  embedded = false,
  onBack
}) => {
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const user = propUser || authUser;

  const [application, setApplication] = useState(propApplication || null);
  const [loading, setLoading] = useState(!propApplication);
  const [error, setError] = useState(null);
  const [chatRefresh, setChatRefresh] = useState(0);

  const applicationId = propApplication?._id || urlId;

  const fetchApplication = useCallback(async () => {
    if (!applicationId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/projects/${applicationId}`);
      setApplication(data);
      setError(null);
    } catch (err) {
      console.error('Error loading application for revision:', err);
      setError(err.response?.data?.error || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    if (propApplication) {
      setApplication(propApplication);
      setLoading(false);
      return;
    }
    fetchApplication();
  }, [propApplication, fetchApplication]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate('/projects/revision-required');
  };

  const handleSaved = async () => {
    setChatRefresh(n => n + 1);
    await fetchApplication();
  };

  if (loading) {
    return <div className="single-application-review" style={{ padding: 24 }}>Loading application...</div>;
  }

  if (error || !application) {
    return (
      <div className="single-application-review" style={{ padding: 24 }}>
        <p>{error || 'Application not found'}</p>
        <button type="button" onClick={handleBack}>Back</button>
      </div>
    );
  }

  const status = application.project_status;
  const isRevision = REVISION_STATUSES.includes(status);
  const isResourceOnly = application.application_type === 'resource_only';

  if (!isRevision) {
    return (
      <div className="single-application-review" style={{ padding: 24 }}>
        <p>This application is not awaiting revision (current status: {status}).</p>
        <button type="button" onClick={handleBack}>Back</button>
      </div>
    );
  }

  return (
    <div className={`single-application-review ${embedded ? 'embedded' : ''}`} style={{ height: 'calc(100vh - 60px)', minHeight: 0 }}>
      <div className="review-two-pane">
        <div className="review-left-pane">
          <div className="review-section-card" style={{ flex: 1, minHeight: 0 }}>
            <div className="review-section-header">
              <span className="review-section-title">Application</span>
              <span className={`review-status-pill status-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                {status}
              </span>
              <button
                type="button"
                className="home-section-action-btn"
                onClick={handleBack}
                style={{ marginLeft: 'auto', background: '#fff', color: '#1e293b' }}
              >
                Back
              </button>
            </div>
            <div className="review-section-body" style={{ overflow: 'auto' }}>
              <div style={{
                margin: '12px 16px 0',
                padding: '10px 12px',
                background: '#fff7ed',
                border: '1px solid #fdba74',
                borderRadius: 8,
                fontSize: '0.85rem',
                color: '#9a3412',
                lineHeight: 1.45
              }}>
                An administrator asked for changes. Edit the form, use the chat on the right
                to discuss, and save your updates. Saving does <strong>not</strong> resubmit
                the application — it stays in Revision Required until an admin approves or rejects it.
              </div>
              <div className="review-form-scroll">
                {isResourceOnly ? (
                  <ResourceOnlyApplication
                    user={user}
                    mode="edit"
                    applicationId={application._id}
                    initialData={application}
                    onSaved={handleSaved}
                    revisionMode
                  />
                ) : (
                  <NewApplication
                    user={user}
                    mode="edit"
                    applicationId={application._id}
                    initialData={application}
                    hideFormHeading
                    onSaved={handleSaved}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="review-right-pane">
          <div className="review-section-card review-chat-card expanded" style={{ flex: 1, minHeight: 0 }}>
            <div className="review-section-header">
              <span className="review-section-title">Revision Discussion</span>
            </div>
            <div className="review-chat-body">
              <ProjectChat
                projectId={application._id}
                user={user}
                maxHeight="100%"
                refreshTrigger={chatRefresh}
                channel="activity"
                allowComments
                commentPlaceholder="Reply to the administrator about these revisions..."
                hideTeamReminder
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRevisionReview;
